// import "@vcl/style.css";
import "@/styles/style.scss";
import { createPinia, storeToRefs } from "pinia";
import { watch } from "vue";
import {
  init,
  axiosInit,
  AxiosPiniaPlugin,
  InjectedDataPlugin,
  SmartFeedError,
  loadFonts,
} from "@bloglovin/vue-framework";
import pages from "@/pages";
import { announceSocialConnectReturn } from "%/helpers/social-connect-return";
import { useGeneralStore } from "%/stores/general-store";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import { usePaymentStore } from "%/stores/payments-store";
import { useSocialNetworksStore } from "@/stores/social-networks-store";
import VCL from "@vcl";
import { useStyleStore } from "@vcl";
import { useIconStore } from "@vcl";

import icons from "%/icons";
import SmartFeedEngine from "@/smart-feed/smart-feed-engine";
import { useFeatureStore } from "%/stores/feature-store";
import { useConfigurationStore } from "%/stores/configuration-store";
import { useUserPreferenceStore } from "@/stores/user-preference-store";
import useBrandingStore from "%/stores/branding-store";
import basicThemePalette from "%/themes/basic-theme-palette";
import basicSemanticPalette from "%/themes/basic-semantic-palette";
import basicTheme from "%/themes/basic-theme";
import { useCollaboratorStore } from "@/stores/collaborator-store";
import { installRenderSurfaceHeader } from "%/dynamic-component-editor/helpers/render-surface-header";
import { startCrashReporting } from "%/crash-reporting/crash-reporting";

axiosInit(window.blclient.data.JWT, window.blclient.data.links.api);
startCrashReporting("Web");
installRenderSurfaceHeader();

const preloadSmartFeedEngineSources = async function (piniaInstance, piniaDataToBeInjected) {
  const smartFeedEngine = new SmartFeedEngine();

  for (const piniaStoreOrFeedName of Object.keys(piniaDataToBeInjected ?? {})) {
    if (piniaStoreOrFeedName.startsWith("use")) {
      let piniaStoreFileName = piniaStoreOrFeedName.replace(/^use/, "").replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
      await Promise.any([
        import(`SmartFeed/stores/feed/${piniaStoreFileName}`),
        import(`@/smart-feed/stores/feed/${piniaStoreFileName}`),
      ]).then(resolvedStoreFile => {
        const storeInstance = resolvedStoreFile[piniaStoreOrFeedName](piniaInstance);
        storeInstance.handleSmartFeedResponse(piniaDataToBeInjected[piniaStoreOrFeedName], smartFeedEngine);
      }).catch(error => {
        throw new SmartFeedError("Unable to find smart feed pinia store to preload smart feed data", {
          store: piniaStoreFileName,
        }, error);
      });
    } else {
      const piniaStoreFunction = SmartFeedEngine.getFeedPiniaStore(piniaStoreOrFeedName);
      const piniaStore = piniaStoreFunction(piniaInstance);
      piniaStore.handleSmartFeedResponse(piniaDataToBeInjected[piniaStoreOrFeedName], smartFeedEngine);
    }
  }
};

const preloadSmartFeedEngineEntities = function(piniaInstance, piniaDataToBeInjected) {
  if (!piniaDataToBeInjected) {
    return;
  }

  const smartFeedEngine = new SmartFeedEngine();
  smartFeedEngine.addEntities(piniaDataToBeInjected);
};

const preloadAppSmartFeedData = function(piniaInstance, apps) {
  if (!apps) {
    return;
  }

  Object.keys(apps).map(appName => {
    Object.keys(apps[appName]).map(async useStoreName => {
      let piniaStoreFileName = useStoreName
          .replace(/^use/, "")
          .replace(/([a-z])([A-Z])/g, "$1-$2")
          .toLowerCase();

      await import(`%/apps/${appName}/stores/${piniaStoreFileName}`).then(
        resolvedStoreFile => {
          const storeInstance = resolvedStoreFile[useStoreName](piniaInstance);
          storeInstance.$injectCustomProperties(apps[appName][useStoreName]);
        },
      );
    });
  });
};

const preloadStoreData = async function (piniaInstance, piniaDataToBeInjected) {
  for (const storeOrEngine of Object.keys(piniaDataToBeInjected)) {
    if (storeOrEngine.startsWith("use")) {
      let piniaStoreFileName = storeOrEngine
          .replace(/^use/, "")
          .replace(/([a-z])([A-Z])/g, "$1-$2")
          .toLowerCase();
      try {
        await import(`@/stores/${piniaStoreFileName}`).then(
          resolvedStoreFile => {
            const storeInstance =
                  resolvedStoreFile[storeOrEngine](piniaInstance);
            storeInstance.$injectCustomProperties(
              piniaDataToBeInjected[storeOrEngine],
            );
          },
        );
      } catch (error) {
        try {
          await import(`%/stores/${piniaStoreFileName}`).then(
            resolvedStoreFile => {
              const storeInstance =
                    resolvedStoreFile[storeOrEngine](piniaInstance);
              storeInstance.$injectCustomProperties(
                piniaDataToBeInjected[storeOrEngine],
              );
            },
          );
        } catch (e) {
          await import(`%/apps/conversations/stores/${piniaStoreFileName}`).then(
            resolvedStoreFile => {
              const storeInstance =
                    resolvedStoreFile[storeOrEngine](piniaInstance);
              storeInstance.$injectCustomProperties(
                piniaDataToBeInjected[storeOrEngine],
              );
            },
          );
        }
      }
    }
  }
};

const initializationFunction = async function (vuePlugins, piniaDataToBeInjected) {
  const piniaInstance = vuePlugins.pinia;
  await preloadSmartFeedEngineSources(piniaInstance, piniaDataToBeInjected.SmartFeedEngine);
  preloadSmartFeedEngineEntities(piniaInstance, piniaDataToBeInjected.SmartFeedEngineEntities);
  await preloadAppSmartFeedData(piniaInstance, piniaDataToBeInjected.apps);
  delete piniaDataToBeInjected.SmartFeedEngineEntities;
  delete piniaDataToBeInjected.apps;
  delete piniaDataToBeInjected.SmartFeedEngine;
  await preloadStoreData(piniaInstance, piniaDataToBeInjected);

  const iconStore = useIconStore(piniaInstance);
  iconStore.setIcons(icons);

  const userPreferenceStore = useUserPreferenceStore(piniaInstance);
  if (window.blclient.data.userPreferences) {
    userPreferenceStore.setUserPreferences(window.blclient.data.userPreferences);
  }
  const userPreferenceAppThemeModes = userPreferenceStore.getUserPreference("appThemeModes");
  const styleStore = useStyleStore(piniaInstance);
  const { appThemeModes } = storeToRefs(styleStore);
  appThemeModes.value = userPreferenceAppThemeModes;

  styleStore.setThemeData(
    styleStore.DEFAULT_THEME_ID,
    basicTheme,
    basicThemePalette,
    basicSemanticPalette,
    styleStore.DEFAULT_APP_ID,
  );

  watch(
    appThemeModes,
    () => {
      const paperBackgroundColor = styleStore.resolveSemanticOrPrimitiveReference(
        styleStore.themes[styleStore.DEFAULT_THEME_ID].color.background.paper,
        styleStore.DEFAULT_THEME_ID,
      );
      document.body.style.backgroundColor = paperBackgroundColor;
      document.documentElement.style.backgroundColor = paperBackgroundColor;
    },
    { immediate: true, deep: true },
  );

  if (window.blclient.data.themes) {
    Object.keys(window.blclient.data.themes).forEach(themeId => {
      if (Number(themeId) === 0) {
        return;
      }
      const theme = window.blclient.data.themes[themeId];
      styleStore.setTheme(theme, themeId);
    });
  }

  if (window.blclient.data.themePalettes) {
    Object.keys(window.blclient.data.themePalettes).forEach(themeId => {
      if (Number(themeId) === 0) {
        return;
      }
      const themePalette = window.blclient.data.themePalettes[themeId];
      styleStore.setThemePalette(themePalette, themeId);
    });
  }

  if (window.blclient.data.semanticPalettes) {
    Object.keys(window.blclient.data.semanticPalettes).forEach(themeId => {
      if (Number(themeId) === 0) {
        return;
      }
      const semanticPalette = window.blclient.data.semanticPalettes[themeId];
      styleStore.setSemanticPalette(semanticPalette, themeId);
    });
  }

  const brandingStore = useBrandingStore(piniaInstance);
  if (window.blclient.data.brandingData) {
    brandingStore.setConfiguration(window.blclient.data.brandingData);
  }
  if (window.blclient.data.templateBrandingData) {
    brandingStore.setDefaultTemplateConfiguration(window.blclient.data.templateBrandingData);
  }
  brandingStore.setPublicationThemeId(window.blclient.data.publicationThemeId ?? 0);

  const generalStore = useGeneralStore(piniaInstance);
  generalStore.setAppId(parseInt(window.blclient.data.appId));
  generalStore.setLinks(window.blclient.data.links);
  generalStore.setBaseDomain(window.blclient.data.baseDomain);

  const socialStore = useSocialNetworksStore(piniaInstance);

  if (window.blclient.data.pageProps["social_networks"]) {
    socialStore.setSocialNetworks(window.blclient.data.pageProps["social_networks"]);
  }

  if (window.blclient.data.pageProps["social_network_connect_result"]) {
    socialStore.setConnectResult(window.blclient.data.pageProps["social_network_connect_result"]);
  }

  const publicationStore = useCreatorPublicationStore(piniaInstance);
  const configurationStore = useConfigurationStore(piniaInstance);

  if (window.blclient.data.publicationData) {
    configurationStore.setPublicationId(window.blclient.data.publicationData.publicationId);
    configurationStore.setPublicationStagingVersionId(window.blclient.data.publicationData.publication.staging_version_id);
  }

  if (window.blclient.data.userId) {
    generalStore.setUserData(window.blclient.data.userData);
    generalStore.setUserId(window.blclient.data.userData.user_id);
    generalStore.setOrgData(window.blclient.data.orgData);
    if (window.blclient.data.adminUserId) {
      generalStore.setAdminUserId(window.blclient.data.adminUserId);
      generalStore.setAdminUserData(window.blclient.data.adminUserData);
    }
    const paymentStore = usePaymentStore(piniaInstance);
    paymentStore.setDigitalWalletData(window.blclient.data.digitalWallet);
  }
  if (window.blclient.data.publicationData) {
    publicationStore.setPublication(window.blclient.data.publicationData);
  }
  if (window.blclient.data.myPublicationsData) {
    publicationStore.setMyPublications(window.blclient.data.myPublicationsData);
  }

  const collaboratorStore = useCollaboratorStore(piniaInstance);
  if (window.blclient.data.publicationRole) {
    collaboratorStore.setRole(window.blclient.data.publicationRole);
  }

  useFeatureStore().updateFeatureConfig(window.blclient.data.featureConfig);
};

const pinia = createPinia();
pinia.use(AxiosPiniaPlugin);
pinia.use(InjectedDataPlugin);

const plugins = {
  pinia: pinia,
  vcl: VCL,
};

const alegreyaSans = loadFonts("Alegreya Sans", [
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/AlegreyaSansNormalLatin400.woff2)",
]);

const nunito = loadFonts("Nunito", [
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoItalicCyrillicExt.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoItalicCyrillic.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoItalicVietnamese.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoItalicLatinExt.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoItalicLatin.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoNormalCyrillicExt.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoNormalCyrillic.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoNormalVietnamese.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoNormalLatinExt.woff2)",
  "url(https://inspirationprod.blob.core.windows.net/inspiration-cdn/fonts/NunitoNormalLatin.woff2)",
]);

await Promise.all([alegreyaSans, nunito]);

announceSocialConnectReturn();
init(process.env.NODE_ENV, process.env.PROJECT_NAME, pages, plugins, initializationFunction, {}, false);
